import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { userHasRight } from "selectors/rights";
import { FIELDREPPROFILE_EDIT } from "typeConstants/rights";
import { MCard, MSpinner } from "components/Shared";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import UploadIcon from "@mui/icons-material/Upload";
import { getBadgePhoto, getBadgePhotoExists, updateBadgePhoto } from "requests/fieldReps/index";

interface Props {
  inspectorNumber: number;
  // redux
  canEdit: boolean;
}

const FieldRepBadge = (props: Props) => {
  const mounted = useRef<boolean>(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  useEffect(() => {
    return () => {
      mounted.current = false;
      if (photoUrl) URL.revokeObjectURL(photoUrl);
    };
  }, []);

  useEffect(() => {
    loadPhoto();
  }, [props.inspectorNumber]);

  const loadPhoto = () => {
    setIsFetching(true);
    if (photoUrl) URL.revokeObjectURL(photoUrl);
    setPhotoUrl(null);

    getBadgePhotoExists(
      props.inspectorNumber,
      (exists: boolean) => {
        if (!exists) {
          setPhotoUrl(null);
          setIsFetching(false);
          return;
        }
        getBadgePhoto(
          props.inspectorNumber,
          (blob: Blob) => {
            const url = URL.createObjectURL(blob);
            setPhotoUrl(url);
            setIsFetching(false);
          },
          () => {
            setPhotoUrl(null);
            setIsFetching(false);
          },
          mounted,
        );
      },
      () => {
        setPhotoUrl(null);
        setIsFetching(false);
      },
      mounted,
    );
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    updateBadgePhoto(
      props.inspectorNumber,
      file,
      () => {
        setIsUploading(false);
        loadPhoto();
      },
      () => {
        setIsUploading(false);
      },
      mounted,
    );

    // Reset input so the same file can be re-selected
    e.target.value = "";
  };

  return (
    <MCard title="Field Rep Badge" isFetching={isUploading}>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, p: 2 }}>
        {isFetching ? (
          <MSpinner />
        ) : photoUrl ? (
          <Box
            component="img"
            src={photoUrl}
            alt="Field Rep Badge"
            sx={{ width: 200, height: 200, objectFit: "cover", borderRadius: 1 }}
          />
        ) : (
          <Box
            sx={{
              width: 200,
              height: 200,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px dashed",
              borderColor: "grey.400",
              borderRadius: 1,
              backgroundColor: "grey.100",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No Image
            </Typography>
          </Box>
        )}
        {props.canEdit && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handleFileChange}
            />
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              size="small"
              onClick={handleUploadClick}
              disabled={isUploading}
            >
              Upload
            </Button>
          </>
        )}
      </Box>
    </MCard>
  );
};

const mapStateToProps = (state: any) => ({
  canEdit: userHasRight(FIELDREPPROFILE_EDIT)(state),
});

export default connect(mapStateToProps)(FieldRepBadge);
